import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import type { DetailedHTMLProps, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ReactComponent as Calendar } from '@pflegia/ui/icons/calendar.svg';
import { ReactComponent as ExclamationCircle } from '@pflegia/ui/icons/exclamation-circle.svg';
import { ReactComponent as EyeHide } from '@pflegia/ui/icons/eye-hide.svg';
import { ReactComponent as EyeShow } from '@pflegia/ui/icons/eye-show.svg';
import { isIos, isNotEnumeratedMobile } from '@pflegia/util/functions/device-detection';

const Close = dynamic(() => import('@mui/icons-material/Close'));

export enum InputState {
  Default = 'default',
  Error = 'error',
}

// TODO: Can be removed and use React.HTMLInputTypeAttribute instead
export enum InputType {
  Date = 'date',
  Email = 'email',
  Month = 'month',
  Number = 'number',
  Password = 'password',
  Phone = 'tel',
  Text = 'text',
}

export enum InputVariant {
  Default = 'default',
  Outline = 'outline',
}

export interface InputProps extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  className?: string;
  label?: string;
  message?: string | ReactNode;
  state?: InputState;
  resettable?: boolean;
  variant?: InputVariant;
  dataTestId?: string;
}

const InputComponent = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      autoComplete,
      className,
      disabled,
      label = '',
      max,
      resettable = false,
      message = '',
      state = InputState.Default,
      type,
      variant = InputVariant.Default,
      dataTestId,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation('common');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const isPassword = type === InputType.Password;
    const resolvedType = isPassword && passwordVisible ? InputType.Text : type;

    const internalRef = useRef<HTMLInputElement | null>(null);
    // Combines internalRef (for DOM operations in this case iOS date picker) with the provided ref (for RHF form state)
    const handleRefCombination = (node: HTMLInputElement | null) => {
      internalRef.current = node;
      if (!ref) return;

      if (typeof ref === 'function') {
        ref(node);
      } else {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    };

    const openDatePicker = () => {
      const inputField = internalRef.current;
      if (!inputField) return;

      if (isIos() && isNotEnumeratedMobile()) {
        inputField.focus();
      } else {
        inputField.showPicker();
      }
    };

    const input = (
      <div
        className={clsx(
          'relative flex w-full rounded-lg shadow-xs transition-shadow',
          resettable && 'pr-4',
          state === InputState.Error
            ? 'ring-error hover:ring-error hover:focus-within:ring-error ring-2'
            : 'ring-input focus-within:ring-input-focus ring-1',
          // The disabled background belongs on the wrapper so the trailing icons sit on it too, see Dropdown.component
          { 'bg-grey': disabled },
          { 'bg-input': !disabled && variant === InputVariant.Default },
          { 'bg-white': !disabled && variant === InputVariant.Outline },
        )}>
        <input
          {...rest}
          autoComplete={autoComplete ?? (isPassword ? 'current-password' : 'off')}
          className={clsx(
            { 'h-11.5 py-2.5': variant === InputVariant.Default },
            { 'h-9 py-3': variant === InputVariant.Outline },
            'text-default before:color-default-icon w-full rounded-lg bg-transparent px-4 opacity-100 outline-hidden placeholder:text-gray-500 disabled:cursor-not-allowed disabled:text-gray-500 disabled:placeholder:text-gray-400',
            type === InputType.Date && 'appearance-none [&::-webkit-calendar-picker-indicator]:hidden',
            isPassword && '[&::-ms-clear]:hidden [&::-ms-reveal]:hidden',
          )}
          disabled={disabled}
          max={max ?? (type === InputType.Date ? '2999-12-31' : undefined)} // bug of date, it was enabling the user to input 6 digit year
          ref={handleRefCombination}
          onWheel={event => type === InputType.Number && (event.target as HTMLInputElement)?.blur()}
          type={resolvedType}
          data-testid={dataTestId}
        />
        {resettable && (
          <div className={clsx('absolute top-0 right-0 h-full w-9 rounded-r-xl text-sm', disabled ? 'bg-grey' : 'bg-input')}>
            <button
              // @ts-expect-error Not all properties needed
              onClick={() => rest.onChange?.({ target: { value: '' } })}
              type='button'
              disabled={disabled}
              className='flex h-full w-full items-center justify-center disabled:cursor-not-allowed disabled:text-gray-400'>
              <span className='sr-only'>Close</span>
              <Close fontSize='inherit' className='absolute' />
            </button>
          </div>
        )}
        {isPassword && (
          <button
            type='button'
            onClick={() => setPasswordVisible(prev => !prev)}
            disabled={disabled}
            className='text-secondary m-auto mr-2 shrink-0 disabled:cursor-not-allowed disabled:text-gray-400'
            aria-label={passwordVisible ? t('hidePassword') : t('showPassword')}
            aria-pressed={passwordVisible}>
            {passwordVisible ? <EyeHide className='h-5.5 w-7' /> : <EyeShow className='h-5.5 w-7' />}
          </button>
        )}
        {type === InputType.Date && (
          <button
            type='button'
            onClick={() => openDatePicker()}
            className='m-auto mr-2 disabled:cursor-not-allowed'
            disabled={disabled}
            aria-label={t('openDatePicker')}>
            <Calendar className={disabled ? 'text-gray-400' : 'text-default-icon'} />
          </button>
        )}
        {state === InputState.Error && <ExclamationCircle className='text-error m-auto mr-2' />}
      </div>
    );

    return (
      <div className={twMerge(clsx({ 'mb-4': !message }, className))}>
        {label ? (
          <label>
            <p className='text-default mb-2 text-sm font-medium'>{label}</p>
            {input}
          </label>
        ) : (
          input
        )}
        {message && (
          <small
            className={clsx('mx-2 mt-2 block text-xs font-medium', state === InputState.Error ? 'text-error' : 'text-secondary')}
            role={state === InputState.Error ? 'alert' : undefined}>
            {message}
          </small>
        )}
      </div>
    );
  },
);

InputComponent.displayName = 'InputComponent';

export default InputComponent;
